import { GetPaths, useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Loader, TagGroupSelect, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState, useEffect, useCallback, useRef } from "react";
import { useOrderContext } from "../../../../pages/order/OrderContext";
import { CheckCardError } from "../../../../pages/order/steps/components/CheckCardError";
import { useTranslation } from "react-i18next";

import { formatCheckPriceLabel } from "../../../../utils/formatCheckPriceLabel";
import { resolveOptionPriceIsCatalogMinimum } from "../../../../utils/resolvePriceIsCatalogMinimum";
import { OrderingCheckListChild } from "../../types";
import { SelectFieldDescription } from "./SelectFieldDescription";
import { useCanViewClientPortalPricing } from "../../../permissions/Permissions";

type ExtendedSelectOption =
  components["schemas"]["UmbrellaCheckSelectOption"] & {
    tagValue: string;
    id: number;
  };

export interface CheckCardSelectProps {
  selectFieldDescription?: string[][];
  selectOptions?: components["schemas"]["UmbrellaCheckSelectOption"][];
  onUpdateSelection: (
    checkArguments: Record<string, unknown>,
    selectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][]
  ) => void;
  type: string;
  customerGroup?: string;
  selectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][];
  isDisabled?: boolean;
  currency?: components["schemas"]["CurrencyEnum"];
  selectFieldInformation: components["schemas"]["SelectFieldInformation"];
  isApplicantPay?: boolean;
  checkArguments?: Record<string, unknown>;
  isQuickscreen?: boolean;
  priceIsCatalogMinimum?: boolean;
  checkChildren?: OrderingCheckListChild[];
}

// Only compatible with EXPLICIT_ORDERING
export function CheckCardMultiSelect(props: CheckCardSelectProps) {
  const {
    type,
    onUpdateSelection,
    customerGroup,
    selectFieldInformation,
    selectFieldDescription,
    currency,
    selectedOptions,
    isApplicantPay,
    checkArguments,
    isQuickscreen,
    priceIsCatalogMinimum: parentPriceIsCatalogMinimum,
    checkChildren,
  } = props;
  const { t } = useTranslation();
  const canViewClientPortalPricing = useCanViewClientPortalPricing();
  const { setCheckValidationError } = useOrderContext();

  const { data: selectInfomation, isLoading } = useClientPortalAPI(
    selectFieldInformation.selection_endpoint as GetPaths,
    {
      customer_group_id: customerGroup,
      is_applicant_pay: isApplicantPay ? "true" : undefined,
      is_quickscreen: isQuickscreen ? "true" : undefined,
    }
  );

  // Once API data is fetched,
  // we format the select options into a formatted list of values that are compatible with TagGroupSelect
  const [formattedValues, setFormattedValues] = useState<
    ExtendedSelectOption[]
  >([]);
  const [selectedValues, setSelectedValues] = useState<ExtendedSelectOption[]>(
    []
  );
  const hasInitialized = useRef(false);
  const lastSyncedSelectionKey = useRef<string | null>(null);
  const prevExplicitTypesRef = useRef<string[] | undefined>(undefined);
  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;

  const selectionParamsKey = JSON.stringify(
    selectFieldInformation?.selection_parameters
  );

  // Reset selected values when the radio selection changes
  useEffect(() => {
    setSelectedValues([]);
    hasInitialized.current = false;
    lastSyncedSelectionKey.current = null;
  }, [selectionParamsKey]);

  const handleOnUpdateSelection = useCallback(
    (
      checkArguments: Record<string, unknown>,
      selectedOptions?: components["schemas"]["UmbrellaCheckSelectOption"][]
    ) => {
      onUpdateSelection(checkArguments, selectedOptions);
    },
    [onUpdateSelection]
  );

  const handleOnUpdateSelectionRef = useRef(handleOnUpdateSelection);
  useEffect(() => {
    handleOnUpdateSelectionRef.current = handleOnUpdateSelection;
  }, [handleOnUpdateSelection]);

  const resolveConflicts = useCallback(
    (newSelection: ExtendedSelectOption[]) => {
      if (newSelection.length === 0) return [];

      // The *last* selected item is the one the user just picked
      const newest = newSelection[newSelection.length - 1];

      const newestTypes = Array.isArray(newest.value) ? newest.value : [];
      const newestConflicts = newest.mutually_exclusive_check_types ?? [];

      // Everything except the newest item
      const previous = newSelection.slice(0, -1);

      // Filter out conflicting previous items
      const filtered = previous.filter((prev) => {
        if (!Array.isArray(prev.value)) return true;

        const prevTypes = prev.value;
        const prevConflicts = prev.mutually_exclusive_check_types ?? [];

        const prevConflictsWithNewest = prevTypes.some((t) =>
          newestConflicts.includes(t)
        );

        const newestConflictsWithPrev = newestTypes.some((t) =>
          prevConflicts.includes(t)
        );

        return !(prevConflictsWithNewest || newestConflictsWithPrev);
      });

      // Put back the newest item
      return [...filtered, newest];
    },
    []
  );

  const getValidationError = useCallback(() => {
    if (selectedValues.length === 0) {
      return t("Please select an option.");
    }

    return null;
  }, [selectedValues, t]);

  useEffect(() => {
    const currentType = type;

    if (props.isDisabled) {
      setCheckValidationError(currentType, false);
    } else {
      const error = getValidationError();
      setCheckValidationError(currentType, !!error);
    }

    return () => {
      setCheckValidationError(currentType, false);
    };
  }, [getValidationError, setCheckValidationError, type, props.isDisabled]);

  useEffect(() => {
    const currentOrderingErrorKey = `${type}_ordering_error`;
    const hasOrderingError =
      !props.isDisabled &&
      selectedValues.some(
        (v) => v.option_has_ordering_error_for_customer_group?.type === "error"
      );
    setCheckValidationError(currentOrderingErrorKey, hasOrderingError);
    return () => {
      setCheckValidationError(currentOrderingErrorKey, false);
    };
  }, [selectedValues, setCheckValidationError, type, props.isDisabled]);

  useEffect(() => {
    if (!hasInitialized.current) {
      return;
    }

    if (props.isDisabled) {
      return;
    }

    if (
      selectedValues.length === 0 &&
      lastSyncedSelectionKey.current === null
    ) {
      return;
    }

    const sanitizedSelectedValues = selectedValues.map(
      ({ tagValue, id, ...rest }) => rest
    );

    const isTemplateMode =
      selectFieldInformation?.selection_parameters?.mode === "template";
    const selected = selectedValues.map((value) => String(value.value));
    const nextCheckArguments = {
      ...(isTemplateMode
        ? { template_ids: selected }
        : {
            ...selectFieldInformation?.selection_parameters,
            ordering_type: "EXPLICIT_ORDERING",
            explicit_check_type: selected,
          }),
    };
    const nextSyncKey = JSON.stringify({
      checkArguments: nextCheckArguments,
      selectedOptions: sanitizedSelectedValues,
    });

    if (lastSyncedSelectionKey.current === nextSyncKey) {
      return;
    }

    lastSyncedSelectionKey.current = nextSyncKey;

    handleOnUpdateSelectionRef.current(
      nextCheckArguments,
      sanitizedSelectedValues
    );
  }, [selectFieldInformation, selectedValues, props.isDisabled]);

  useEffect(() => {
    if (selectInfomation) {
      // Handle both response formats:
      // 1. Wrapped format: { select_options: [...] } from country options endpoint
      // 2. Direct array format: [...] from template options endpoint
      let selectValuesData: components["schemas"]["UmbrellaCheckSelectOption"][];

      if (Array.isArray(selectInfomation)) {
        // Direct array format
        selectValuesData =
          selectInfomation as unknown as components["schemas"]["UmbrellaCheckSelectOption"][];
      } else if ("select_options" in selectInfomation) {
        // Wrapped format
        selectValuesData = (
          selectInfomation as components["schemas"]["CheckOptionsResponse"]
        ).select_options;
      } else {
        selectValuesData = [];
      }

      setFormattedValues(
        selectValuesData?.map((option, index) => {
          const optionPriceLabel = formatCheckPriceLabel(option.price, {
            currency,
            locale: "en-US",
            priceIsCatalogMinimum: resolveOptionPriceIsCatalogMinimum(
              option,
              checkChildren,
              parentPriceIsCatalogMinimum
            ),
            fromPrefix: t("From "),
          });

          return {
            ...option,
            id: index + 1, // Ensure id never starts at 0
            tagValue:
              canViewClientPortalPricing && optionPriceLabel
                ? `${option.label} - ${optionPriceLabel}`
                : option.label,
          };
        }) || []
      );
    }
  }, [
    canViewClientPortalPricing,
    checkChildren,
    currency,
    parentPriceIsCatalogMinimum,
    selectInfomation,
    t,
  ]);

  useEffect(() => {
    // Initialize selectedValues from checkArguments.explicit_check_type or selectedOptions
    // Only run once per mode to prevent re-selecting after user clears all selections
    if (hasInitialized.current) return;

    if (formattedValues.length > 0 && selectedValues.length === 0) {
      // First try checkArguments.explicit_check_type (for endpoint-fetched options)
      if (
        checkArguments?.explicit_check_type &&
        Array.isArray(checkArguments.explicit_check_type)
      ) {
        const matchingValues = formattedValues.filter((value) => {
          const valueItems = Array.isArray(value.value)
            ? value.value
            : [value.value];
          return valueItems.some((v) =>
            (checkArguments.explicit_check_type as string[]).includes(v)
          );
        });
        if (matchingValues.length > 0) {
          setSelectedValues(matchingValues);
          hasInitialized.current = true;
          return;
        }
      }

      // Try checkArguments.template_ids (for template-based checks like CQ in bundles)
      if (
        checkArguments?.template_ids &&
        Array.isArray(checkArguments.template_ids)
      ) {
        const matchingValues = formattedValues.filter((value) => {
          const optionId = String(value.value);
          return (checkArguments.template_ids as string[]).some(
            (tid) => String(tid) === optionId
          );
        });
        if (matchingValues.length > 0) {
          setSelectedValues(matchingValues);
          hasInitialized.current = true;
          return;
        }
      }

      // Fallback to selectedOptions prop
      if (selectedOptions && selectedOptions.length > 0) {
        const matchingValues = formattedValues.filter((value) =>
          selectedOptions.some((option) => option.value === value.value)
        );
        if (matchingValues.length > 0) {
          setSelectedValues(matchingValues);
        }
      }
      hasInitialized.current = true;
    }
  }, [selectedOptions, formattedValues, checkArguments, selectedValues.length]);

  useEffect(() => {
    const currentTypes = checkArguments?.explicit_check_type as
      | string[]
      | undefined;
    const prevTypes = prevExplicitTypesRef.current;
    prevExplicitTypesRef.current = Array.isArray(currentTypes)
      ? [...currentTypes]
      : undefined;

    if (!hasInitialized.current) return;
    if (!Array.isArray(currentTypes) || !Array.isArray(prevTypes)) return;
    if (currentTypes.length === 0) return;
    if (currentTypes.length >= prevTypes.length) return;

    const allowedTypes = new Set(currentTypes);
    const current = selectedValuesRef.current;
    const filtered = current.filter((sv) => {
      const types = Array.isArray(sv.value) ? sv.value : [String(sv.value)];
      return types.some((t) => allowedTypes.has(t));
    });

    if (filtered.length !== current.length) {
      setSelectedValues(filtered);
    }
  }, [checkArguments]);

  if (isLoading) {
    return <Loader />;
  }

  if (!selectInfomation) {
    return <></>;
  }

  return (
    <div
      className={styles.selectListSection}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <TagGroupSelect<
        components["schemas"]["UmbrellaCheckSelectOption"],
        ExtendedSelectOption
      >
        groupType="check"
        testID={`${type}-additional-options`}
        values={formattedValues}
        selectedValues={selectedValues}
        setSelectedValues={(values) => {
          const resolvedValues = resolveConflicts(
            values as ExtendedSelectOption[]
          );
          setSelectedValues(resolvedValues);
        }}
        displayHeader={false}
        allowAddingNewValues={false}
        placeholder={
          selectFieldInformation.select_placeholder ?? t("Select checks")
        }
        emptyMessage={
          selectFieldInformation.no_results_placeholder ?? t("No results found")
        }
        label={selectFieldInformation.selection_description_label}
        isRequired
        isDisabled={props.isDisabled || !formattedValues?.length}
        errors={props.isDisabled ? [] : [getValidationError()].filter(Boolean)}
        isInvalid={!props.isDisabled && getValidationError() !== null}
        info={
          <SelectFieldDescription
            selectFieldDescription={selectFieldDescription}
          />
        }
      />
      {selectedValues.map((sv) =>
        sv.option_has_ordering_error_for_customer_group ? (
          <CheckCardError
            error={sv.option_has_ordering_error_for_customer_group}
          />
        ) : null
      )}
      {selectFieldDescription &&
        selectFieldDescription[2] &&
        selectFieldDescription[2][0] && (
          <Text style="global/info-label" color="text-weak">
            {selectFieldDescription[2][0]}
          </Text>
        )}
    </div>
  );
}

const styles = {
  selectListSection: css({
    padding: `${"var(--space-2)"} ${"var(--space-3)"} ${"var(--space-3)"}`,
    margin: "var(--space-0) var(--space-05)",
    gap: "var(--space-2)",
    backgroundColor: "var(--background-accent-neutral-weakest)",
    display: "flex",
    flexDirection: "column",
  }),
};
